import { useEffect, useState } from "react";
import { api, formatPrice, errMsg, effectivePrice } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ShoppingBasket, Minus, Plus, MapPin, Users } from "lucide-react";
import { toast } from "sonner";

export function CartDrawer({ menu, cart, cartCount, table, onChangeQty, onSubmit }) {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [people, setPeople] = useState(1);
  const [coverPrice, setCoverPrice] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) api.get("/settings").then((r) => setCoverPrice(r.data.cover_price || 0)).catch(() => {});
  }, [open]);

  const entries = Object.entries(cart)
    .map(([id, qty]) => ({ item: menu.find((m) => m.item_id === id), qty }))
    .filter((e) => e.item);
  const itemsTotal = entries.reduce((s, e) => s + effectivePrice(e.item) * e.qty, 0);
  const coverTotal = people * coverPrice;
  const total = itemsTotal + coverTotal;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(note, people);
      setNote("");
      setPeople(1);
      setOpen(false);
    } catch (e) {
      if (e?.response) toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="cart-drawer-trigger"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-[#D97706] hover:bg-[#b45309] text-white rounded-full pl-4 pr-5 py-3 shadow-xl transition-transform hover:scale-105"
        >
          <ShoppingBasket className="w-5 h-5" />
          <span className="font-bold text-sm">{cartCount}</span>
          {cartCount > 0 && <span className="text-sm font-semibold">{formatPrice(itemsTotal + people * coverPrice)}</span>}
        </button>
      </SheetTrigger>
      <SheetContent className="bg-[#FDFBF7] w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading text-[#4A2C2A]">{t("cart_title")}</SheetTitle>
        </SheetHeader>
        {entries.length === 0 ? (
          <p className="text-[#786C66] text-sm mt-6" data-testid="cart-empty">{t("cart_empty")}</p>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto mt-4 space-y-3 pr-1">
              {entries.map(({ item, qty }) => (
                <div key={item.item_id} className="flex items-center justify-between gap-2 bg-white border border-[#E8DFD5] rounded-xl p-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#1F1615] truncate">{lang === "it" ? item.name_it : item.name_en}</p>
                    <p className="text-xs text-[#786C66]">{formatPrice(effectivePrice(item))}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`cart-minus-${item.item_id}`} onClick={() => onChangeQty(item.item_id, -1)} className="w-7 h-7 rounded-full border border-[#E8DFD5] flex items-center justify-center text-[#4A2C2A] hover:bg-[#F6F1E9]">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="font-bold text-sm w-5 text-center">{qty}</span>
                    <button data-testid={`cart-plus-${item.item_id}`} onClick={() => onChangeQty(item.item_id, 1)} className="w-7 h-7 rounded-full border border-[#E8DFD5] flex items-center justify-center text-[#4A2C2A] hover:bg-[#F6F1E9]">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="pt-4 space-y-3">
              <div className="flex items-center gap-3 bg-white border border-[#E8DFD5] rounded-xl p-3">
                <Users className="w-4 h-4 text-[#D97706] shrink-0" />
                <span className="text-sm font-semibold text-[#1F1615]">{t("people")}</span>
                <select
                  data-testid="people-select"
                  value={people}
                  onChange={(e) => setPeople(parseInt(e.target.value, 10))}
                  className="ml-auto h-9 rounded-md border border-[#E8DFD5] bg-white px-3 text-sm font-semibold"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <Textarea
                data-testid="order-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("note_placeholder")}
                className="bg-white border-[#E8DFD5] resize-none"
                rows={2}
              />
              <div className="text-sm space-y-1">
                <div className="flex justify-between text-[#786C66]">
                  <span>{t("subtotal")}</span>
                  <span>{formatPrice(itemsTotal)}</span>
                </div>
                <div className="flex justify-between text-[#786C66]" data-testid="cover-line">
                  <span>{t("cover")} ({people} × {formatPrice(coverPrice)})</span>
                  <span>{formatPrice(coverTotal)}</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-[#E8DFD5]">
                  <span className="flex items-center gap-1 text-[#786C66]">
                    <MapPin className="w-4 h-4 text-[#D97706]" />
                    {table ? `${t("table")} ${table}` : t("select_table")}
                  </span>
                  <span className="font-heading font-bold text-lg text-[#1F1615]">
                    {t("total")}: {formatPrice(total)}
                  </span>
                </div>
              </div>
              <Button
                data-testid="submit-order-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7] h-11 font-semibold"
              >
                {t("place_order")} — {formatPrice(total)}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
